import React, { useEffect, useState } from 'react';
import { Shield, KeyRound } from 'lucide-react';
import service from '../../services/reportingApiService';
import './ReportingApiPage.css';

const errorText = (error) => {
  const data = error.response?.data;
  if (typeof data?.detail === 'string') return data.detail;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return 'Impossible de terminer cette opération. Réessayez.';
};

export default function ReportingApiPage() {
  const [settings, setSettings] = useState(null);
  const [allCentres, setAllCentres] = useState(false);
  const [centreIds, setCentreIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [key, setKey] = useState('');
  const [confirmRenew, setConfirmRenew] = useState(false);

  const load = async () => {
    setBusy(true); setError('');
    try {
      const result = await service.getSettings();
      setSettings(result); setAllCentres(result.all_centres); setCentreIds(result.centre_ids);
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (values, success) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await service.saveSettings(values);
      setSettings(result); setMessage(success);
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  const rotate = async () => {
    setBusy(true); setError(''); setMessage(''); setConfirmRenew(false);
    try {
      const { key: secret, ...result } = await service.rotateKey();
      setKey(secret); setSettings(result);
      setMessage('Nouvelle clé générée. Conservez-la avant de quitter cette page.');
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  const copy = async (value, success) => {
    try { await navigator.clipboard.writeText(value); setMessage(success); }
    catch { setError('Copie indisponible. Sélectionnez le texte pour le copier manuellement.'); }
  };
  const toggleCentre = (id) => setCentreIds((ids) => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const canActivate = settings?.has_key && (settings.all_centres || settings.centre_ids.length > 0);

  return <main className="reporting-admin">
    <header className="reporting-admin-header">
      <Shield aria-hidden="true" size={28} />
      <div><h1>API Reporting</h1><p>Gérez l’accès de la plateforme de reporting aux données du PMS.</p></div>
    </header>
    {error && <div className="reporting-notice reporting-error" role="alert">{error}</div>}
    {message && <div className="reporting-notice" role="status">{message}</div>}
    {!settings ? <div className="reporting-panel">
      {busy ? <p role="status">Chargement des réglages…</p> : <button onClick={load}>Réessayer</button>}
    </div> : <>
      <section className="reporting-panel reporting-status">
        <div><h2>Accès à l’API</h2><p><strong>{settings.enabled ? 'Activée' : 'Désactivée'}</strong></p>
          <p>Les modifications prennent effet immédiatement pour les nouveaux appels.</p></div>
        <button disabled={busy || (!settings.enabled && !canActivate)}
          onClick={() => save({ enabled: !settings.enabled }, settings.enabled ? 'API désactivée.' : 'API activée.')}>
          {settings.enabled ? 'Désactiver l’API' : 'Activer l’API'}
        </button>
        {!settings.enabled && !canActivate && <p className="reporting-hint">Pour activer l’API, générez une clé et enregistrez les centres accessibles.</p>}
      </section>
      <section className="reporting-panel">
        <h2>Centres accessibles</h2>
        <fieldset disabled={busy}>
          <legend className="reporting-sr-only">Choisir les centres autorisés</legend>
          <label className="reporting-choice"><input type="checkbox" checked={allCentres} onChange={e => setAllCentres(e.target.checked)} /> Tous les centres, y compris les futurs centres</label>
          {!allCentres && <div className="reporting-centres">{settings.available_centres.length ? settings.available_centres.map(centre =>
            <label className="reporting-choice" key={centre.id}><input type="checkbox" checked={centreIds.includes(centre.id)} onChange={() => toggleCentre(centre.id)} />{centre.name}</label>
          ) : <p>Aucun centre disponible.</p>}</div>}
          <button onClick={() => save({ all_centres: allCentres, centre_ids: centreIds }, 'Centres accessibles enregistrés.')}>Enregistrer les centres</button>
        </fieldset>
      </section>
      <section className="reporting-panel">
        <h2><KeyRound size={20} aria-hidden="true" /> Clé d’accès</h2>
        <p>{settings.has_key ? 'Une clé est configurée. Elle ne peut pas être réaffichée.' : 'Aucune clé n’a encore été générée.'}</p>
        {settings.key_rotated_at && <p>Dernière génération : {new Date(settings.key_rotated_at).toLocaleString('fr-FR')}</p>}
        <button disabled={busy} onClick={() => settings.has_key ? setConfirmRenew(true) : rotate()}>{settings.has_key ? 'Renouveler la clé' : 'Générer une clé'}</button>
        {confirmRenew && <div className="reporting-notice" role="group" aria-label="Confirmation du renouvellement">
          <p>La clé actuelle cessera immédiatement de fonctionner. Il faudra remplacer la clé dans la plateforme de reporting.</p>
          <div className="reporting-actions"><button disabled={busy} onClick={rotate}>Confirmer le renouvellement</button><button disabled={busy} onClick={() => setConfirmRenew(false)}>Annuler</button></div>
        </div>}
        {key && <div className="reporting-secret">
          <label htmlFor="reporting-key">Nouvelle clé — affichée une seule fois</label>
          <textarea id="reporting-key" readOnly value={key} rows={2} spellCheck={false} />
          <p>Copiez-la dans la plateforme de reporting. Elle disparaît lorsque vous quittez cette page.</p>
          <div className="reporting-actions"><button onClick={() => copy(key, 'Clé copiée.')}>Copier la clé</button><button onClick={() => { setKey(''); setMessage('Clé masquée.'); }}>J’ai conservé la clé</button></div>
        </div>}
      </section>
      <section className="reporting-panel">
        <h2>Adresse de l’API</h2><p>Sessions vérifiées et validées, sélectionnées selon leur mois de début. Accès en lecture seule.</p>
        <label className="reporting-sr-only" htmlFor="reporting-url">Adresse de l’API</label>
        <input id="reporting-url" className="reporting-url" value={settings.endpoint_url || ''} readOnly />
        <button onClick={() => copy(settings.endpoint_url, 'Adresse copiée.')}>Copier l’adresse</button>
      </section>
    </>}
  </main>;
}
